export enum AssessmentBand {
  CRITICAL = "CRITICAL",
  STRESSED = "STRESSED",
  WATCH = "WATCH",
  STABLE = "STABLE",
  SOUND = "SOUND",
  STRONG = "STRONG",
  UNCLEAR = "UNCLEAR",
}

// The bands from most to least severe, with UNCLEAR between the adverse and
// the favorable bands: the order the swarm, the sorts and the tables read.
export const BAND_SPECTRUM: AssessmentBand[] = [
  AssessmentBand.CRITICAL,
  AssessmentBand.STRESSED,
  AssessmentBand.WATCH,
  AssessmentBand.UNCLEAR,
  AssessmentBand.STABLE,
  AssessmentBand.SOUND,
  AssessmentBand.STRONG,
]

// The shared sentence that introduces every band's definition, wherever the
// bands are explained to a buyer (CEO-approved Policy 2.0 wording).
export const ASSESSMENT_INTRO =
  "Night Shift assessments are evidence-based judgments of a contractor’s counterparty risk, based on its treatment of customers and business standing."

// Colors reference the terminal assessment spectrum (terminal-theme.css), so
// bands follow the active theme; valid in inline styles and color-mix(), not
// as raw SVG presentation attributes. Definitions are the CEO-approved Policy
// 2.0 wording, the one copy every buyer surface reads. `scoreRange` restates
// the policy's band ranges for display (a test holds it to the policy); UNCLEAR
// has no score. An UNCLEAR File's own reason comes from `dataByUnclearReason`.
export const dataByAssessmentBand: Record<
  AssessmentBand,
  {
    label: string
    color: string
    scoreRange: string | null
    definition: string
  }
> = {
  [AssessmentBand.CRITICAL]: {
    label: "Critical",
    color: "var(--terminal-assessment-critical)",
    scoreRange: "0–49",
    definition:
      "Counterparty risk assessed as severe, supported by institutional evidence of operating or financial distress, or an adverse record of comparable severity.",
  },
  [AssessmentBand.STRESSED]: {
    label: "Stressed",
    color: "var(--terminal-assessment-stressed)",
    scoreRange: "50–59",
    definition:
      "Counterparty risk assessed as substantial, with serious concerns about treatment of customers or business standing.",
  },
  [AssessmentBand.WATCH]: {
    label: "Watch",
    color: "var(--terminal-assessment-watch)",
    scoreRange: "60–69",
    definition:
      "Counterparty risk assessed as elevated, with material concerns about treatment of customers or business standing.",
  },
  [AssessmentBand.STABLE]: {
    label: "Stable",
    color: "var(--terminal-assessment-stable)",
    scoreRange: "70–79",
    definition:
      "Counterparty risk assessed as neutral. Favorable evidence is limited or offset by adverse concerns, or uncertainty prevents a more favorable assessment.",
  },
  [AssessmentBand.SOUND]: {
    label: "Sound",
    color: "var(--terminal-assessment-sound)",
    scoreRange: "80–89",
    definition:
      "Counterparty risk assessed as low, supported by favorable treatment of customers or business standing.",
  },
  [AssessmentBand.STRONG]: {
    label: "Strong",
    color: "var(--terminal-assessment-strong)",
    scoreRange: "90–100",
    definition:
      "Counterparty risk assessed as very low, supported by exceptionally favorable treatment of customers and limited concerns about business standing.",
  },
  [AssessmentBand.UNCLEAR]: {
    label: "Unclear",
    color: "var(--terminal-assessment-unclear)",
    scoreRange: null,
    definition:
      "Counterparty risk cannot be assessed with sufficient confidence from available evidence.",
  },
}
